// useIntlAccaDebrief — the DebriefEnvelope from the real RPC
// public.intl_acca_tournament_debrief(p_tournament_id) + the
// tournament header. DOCTRINE: never fabricate — the envelope is
// "wired but empty" until Pinnacle closes flow; the page renders that.

import { useEffect, useMemo, useState } from "react";
import { useRpcQuery } from "../data/useRpcQuery.ts";
import { getBrowserClient } from "../supabase/browser.ts";
import type {
  DebriefEnvelope,
  IntlAccaTournament,
} from "../db/intl-acca-types.ts";

export interface IntlAccaDebriefVM {
  loading: boolean;
  error: string | null;
  tournament: IntlAccaTournament | null;
  envelope: DebriefEnvelope | null;
  refetch: () => Promise<void>;
}

export function useIntlAccaDebrief(tournamentId: string): IntlAccaDebriefVM {
  const enabled = Boolean(tournamentId);

  // The debrief envelope comes from a Supabase RPC — wrapped by the
  // shared data layer (cached, refetch-on-focus/return).
  const q = useRpcQuery<DebriefEnvelope | null>(
    "intl_acca_tournament_debrief",
    { args: { p_tournament_id: tournamentId }, enabled },
  );

  // The tournament header is a plain table read (not an RPC), so it
  // stays on the browser client. Kept in lockstep with the envelope id.
  const supabase = useMemo(() => getBrowserClient() as any, []);
  const [tournament, setTournament] = useState<IntlAccaTournament | null>(null);
  const [tLoading, setTLoading] = useState<boolean>(enabled);
  const [tError, setTError] = useState<string | null>(null);

  useEffect(() => {
    if (!tournamentId) {
      setTournament(null);
      setTLoading(false);
      setTError(null);
      return;
    }
    let cancelled = false;
    setTLoading(true);
    setTError(null);
    void (async () => {
      try {
        const res = await supabase
          .from("intl_acca_tournaments")
          .select("*")
          .eq("id", tournamentId)
          .maybeSingle();
        if (cancelled) return;
        if (res.error) throw res.error;
        setTournament((res.data as IntlAccaTournament | null) ?? null);
        setTLoading(false);
      } catch (e) {
        if (cancelled) return;
        setTournament(null);
        setTError(e instanceof Error ? e.message : String(e));
        setTLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [supabase, tournamentId]);

  const refetch = useMemo(
    () => async () => {
      await Promise.all([
        q.refetch(),
        (async () => {
          if (!tournamentId) return;
          setTLoading(true);
          setTError(null);
          try {
            const res = await supabase
              .from("intl_acca_tournaments")
              .select("*")
              .eq("id", tournamentId)
              .maybeSingle();
            if (res.error) throw res.error;
            setTournament((res.data as IntlAccaTournament | null) ?? null);
          } catch (e) {
            setTournament(null);
            setTError(e instanceof Error ? e.message : String(e));
          } finally {
            setTLoading(false);
          }
        })(),
      ]);
    },
    [q, supabase, tournamentId],
  );

  return useMemo(() => {
    if (!enabled) {
      return {
        loading: false,
        error: null,
        tournament: null,
        envelope: null,
        refetch,
      };
    }
    const error = q.error ? q.error.message : tError;
    return {
      loading: q.isLoading || tLoading,
      error,
      tournament: error ? null : tournament,
      envelope: error ? null : (q.data ?? null),
      refetch,
    };
  }, [enabled, q.isLoading, q.error, q.data, tLoading, tError, tournament, refetch]);
}
